import { useState, useEffect } from 'react'
import { withRequiredPermission } from '@/components/auth/with-required-permission'
import { PERMISSIONS } from '@/hooks/use-perm'
import { useNavigate } from 'react-router-dom'
import { useProcessorEditId } from '@/app/processors/hooks/use-processor-edit-id'
import { useProcessorEditExit } from '@/app/processors/hooks/use-processor-edit-exit'
import { useTranslation } from 'react-i18next'
import { AlertTriangle } from 'lucide-react'
import { useProcessorById } from '@/lib/api/processors'
import { useUpdateProcessor } from '@/lib/api/processors'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useToast } from '@/components/ui/use-toast'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { ProcessorEditHeader } from '../../components/processor-edit-header'
import { MerchantScaleEditSkeleton } from '../components/merchantscale-edit-skeleton'
import { ProcessorApmSettings, ApmSettings, initApmSettingsFromOptions, getMorPublishableKey } from '../../components/processor-apm-settings'
import { ProcessorDryModeField } from '../../components/processor-dry-mode-field'
import { MOR_PUBLISHABLE_KEYS, isTestEnvironment } from '@/lib/payment-constants'

const MERCHANTSCALE_RADAR_TEST_KEY = MOR_PUBLISHABLE_KEYS.merchantscale.test
const MERCHANTSCALE_RADAR_PROD_KEY = MOR_PUBLISHABLE_KEYS.merchantscale.live

const MerchantScaleEditPageInner = () => {
    const id = useProcessorEditId()
    const navigate = useNavigate()
    const processorExit = useProcessorEditExit()
    const { t } = useTranslation('processors')
    const { toast } = useToast()

    const { data: processor, isLoading } = useProcessorById(id)
    const updateProcessorMutation = useUpdateProcessor()

    const [validationError, setValidationError] = useState<string | null>(null)
    const [apmSettings, setApmSettings] = useState<ApmSettings>({})
    const [expressPublishableKey, setExpressPublishableKey] = useState<string>('')
    const [formData, setFormData] = useState({
        name: '',
        enabled: true,
        accountId: '',
        descriptorSuffix: '',
        radarEnabled: false,
        radarPublishableKey: '',
        dryMode: false,
    })

    useEffect(() => {
        if (processor) {
            const options = processor.options as any
            const descriptor = options?.descriptor as { descriptor_suffix?: string } | undefined
            const radar = options?.radar as { enabled?: boolean; publishableKey?: string } | undefined

            const radarEnabled = radar?.enabled ?? false
            let radarPublishableKey = radar?.publishableKey || ''
            if (radarEnabled && !radarPublishableKey) {
                radarPublishableKey = isTestEnvironment() ? MERCHANTSCALE_RADAR_TEST_KEY : MERCHANTSCALE_RADAR_PROD_KEY
            }

            setFormData({
                name: processor.name,
                enabled: processor.enabled ?? true,
                accountId: options?.accountId || '',
                descriptorSuffix: descriptor?.descriptor_suffix || '',
                radarEnabled,
                radarPublishableKey,
                dryMode: options?.dryMode === true,
            })
            setApmSettings(initApmSettingsFromOptions('merchantscale', options?.apmSettings as Record<string, Record<string, unknown>> | undefined))
            setExpressPublishableKey(options?.expressPublishableKey || getMorPublishableKey('merchantscale'))
        }
    }, [processor])

    const handleSubmit = async () => {
        if (!formData.name.trim()) {
            setValidationError(t('nameRequired'))
            return
        }

        if (!formData.accountId.trim()) {
            setValidationError(t('accountIdRequired') || 'Account ID is required')
            return
        }

        if (!formData.descriptorSuffix.trim()) {
            setValidationError(t('descriptorSuffixRequired'))
            return
        }

        setValidationError(null)

        try {
            await updateProcessorMutation.mutateAsync({
                processorId: processor!.id,
                updatedData: {
                    type: processor!.type,
                    name: formData.name,
                    enabled: formData.enabled,
                    options: {
                        accountId: formData.accountId,
                        descriptor: {
                            descriptor_suffix: formData.descriptorSuffix,
                        },
                        radar: {
                            enabled: formData.radarEnabled,
                            publishableKey: formData.radarPublishableKey,
                        },
                        apmSettings,
                        expressPublishableKey: expressPublishableKey || undefined,
                        dryMode: formData.dryMode,
                    },
                } as any,
            })

            toast({ description: t('processorUpdated') })
            if (processorExit.isEmbed) {
                processorExit.exitAfterSave()
                return
            }
            navigate(-1)
        } catch (error) {
            console.error('Error saving processor:', error)
            toast({ description: t('failedToSave'), variant: 'destructive' })
        }
    }

    const handleCancel = () => {
        if (processorExit.isEmbed) {
            processorExit.exitCancel()
            return
        }
        navigate(-1)
    }

    if (isLoading) {
        return (
            <div className="container mx-auto py-8">
                <MerchantScaleEditSkeleton />
            </div>
        )
    }

    if (!processor || processor.type !== 'merchantscale') {
        return (
            <div className="container mx-auto py-8">
                <Alert variant="destructive">
                    <AlertDescription>{t('processorNotFound')}</AlertDescription>
                </Alert>
            </div>
        )
    }

    return (
        <div className="container mx-auto py-8">
            <ProcessorEditHeader
                processorId={processor.id}
                title={t('merchantscale.editTitle')}
                onCancel={handleCancel}
                onSave={handleSubmit}
                isSaving={updateProcessorMutation.isPending}
            />

            {validationError && (
                <Alert variant="destructive" className="mb-6">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>{validationError}</AlertDescription>
                </Alert>
            )}

            <Tabs defaultValue="configuration">
                <TabsList className="mb-6">
                    <TabsTrigger value="configuration">{t('merchantscale.tabs.configuration')}</TabsTrigger>
                    <TabsTrigger value="payment-methods">{t('merchantscale.tabs.paymentMethods')}</TabsTrigger>
                </TabsList>

                <TabsContent value="configuration">
                    <div className="space-y-6">
                        <Card>
                            <CardHeader>
                                <CardTitle>{t('generalSettings')}</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div>
                                    <Label htmlFor="name">{t('processorName')}</Label>
                                    <Input
                                        id="name"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    />
                                </div>

                                <div className="flex items-center space-x-2">
                                    <Checkbox
                                        id="enabled"
                                        checked={formData.enabled}
                                        onCheckedChange={(checked) =>
                                            setFormData({ ...formData, enabled: checked === true })
                                        }
                                    />
                                    <Label htmlFor="enabled">{t('enabled')}</Label>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle>{t('merchantScaleConfiguration')}</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div>
                                    <Label htmlFor="accountId">{t('merchantScaleAccountId')} *</Label>
                                    <Input
                                        id="accountId"
                                        value={formData.accountId}
                                        onChange={(e) => setFormData({ ...formData, accountId: e.target.value })}
                                        placeholder="acct_..."
                                    />
                                    <p className="text-sm text-muted-foreground mt-1">
                                        {t('merchantScaleAccountIdDescription')}
                                    </p>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle>{t('statementDescriptor')}</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div>
                                    <Label htmlFor="descriptorSuffix">{t('descriptorSuffix')} *</Label>
                                    <Input
                                        id="descriptorSuffix"
                                        value={formData.descriptorSuffix}
                                        onChange={(e) => {
                                            const value = e.target.value.toUpperCase()
                                            setFormData({ ...formData, descriptorSuffix: value })
                                        }}
                                        maxLength={12}
                                        placeholder="e.g., COFFEE SHOP"
                                    />
                                    <p className="text-sm text-muted-foreground mt-1">
                                        {t('merchantScaleDescriptorSuffixDescription')}
                                    </p>
                                </div>

                                <div className="rounded-lg bg-blue-50 p-4 border border-blue-200">
                                    <p className="text-sm text-blue-700">
                                        <strong>{t('exampleStatement')}:</strong> MERCHANTSCALE*{formData.descriptorSuffix || 'SUFFIX'}
                                    </p>
                                    <p className="text-xs text-blue-600 mt-2">
                                        {t('merchantScaleDescriptorMaxLength')}
                                    </p>
                                </div>

                                <div className="space-y-2">
                                    <p className="text-sm text-muted-foreground">
                                        <strong>{t('requirementsForSuffix')}:</strong>
                                    </p>
                                    <ul className="list-disc pl-4 text-sm text-muted-foreground space-y-1">
                                        <li>{t('suffixRequirement1')}</li>
                                        <li>{t('suffixRequirement2')}</li>
                                        <li>{t('suffixRequirement3')}</li>
                                        <li>{t('suffixRequirement4')}</li>
                                    </ul>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle>{t('radarFraudProtection')}</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="flex items-center space-x-2">
                                    <Checkbox
                                        id="radarEnabled"
                                        checked={formData.radarEnabled}
                                        onCheckedChange={(checked) => {
                                            const isEnabled = checked === true
                                            const publishableKey = isEnabled
                                                ? isTestEnvironment()
                                                    ? MERCHANTSCALE_RADAR_TEST_KEY
                                                    : MERCHANTSCALE_RADAR_PROD_KEY
                                                : formData.radarPublishableKey

                                            setFormData({
                                                ...formData,
                                                radarEnabled: isEnabled,
                                                radarPublishableKey: publishableKey,
                                            })
                                        }}
                                    />
                                    <Label htmlFor="radarEnabled">{t('enableRadar')}</Label>
                                </div>

                                {formData.radarEnabled && (
                                    <div className="space-y-4">
                                        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                                            <p className="text-sm text-blue-700">
                                                {t('radarEnabledDescription')}
                                            </p>
                                        </div>

                                        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                                            <p className="text-sm font-medium text-blue-700">
                                                {t('radarFeatures')}
                                            </p>
                                            <ul className="mt-1 list-disc pl-4 text-xs text-blue-700">
                                                <li>{t('radarFeature1')}</li>
                                                <li>{t('radarFeature2')}</li>
                                                <li>{t('radarFeature3')}</li>
                                                <li>{t('radarFeature4')}</li>
                                            </ul>
                                        </div>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                        <ProcessorDryModeField
                            checked={formData.dryMode}
                            onCheckedChange={(checked) => setFormData({ ...formData, dryMode: checked })}
                            disabled={updateProcessorMutation.isPending}
                        />
                    </div>
                </TabsContent>

                <TabsContent value="payment-methods">
                    <ProcessorApmSettings
                        processorType="merchantscale"
                        value={apmSettings}
                        onChange={setApmSettings}
                        expressPublishableKey={expressPublishableKey}
                        disabled={updateProcessorMutation.isPending}
                    />
                </TabsContent>
            </Tabs>
        </div>
    )
}

export const MerchantScaleEditPage = withRequiredPermission(MerchantScaleEditPageInner, PERMISSIONS.PROCESSORS_MANAGE)
